// Makes the Quality Control PDF (deductionsPdf.ts) as a background task: reads the
// deducted picks for the report's window and contractor, then for each the
// quality scan's photos and a map of where it was picked: Google's satellite
// imagery where the build has a Maps key, as on the app's maps
// (sites/googleTiles.ts), else the street tiles (sites/mapTiles.ts). A photo or
// map that will not load is left out and the PDF says so; the file is still
// made.

import type { StartTask } from '../../components/TaskCentre'
import { apiFetch, apiJson } from '../../lib/api'
import { googleSession, TILE_BASE } from '../sites/googleTiles'
import { googleMapsKey, tileSource } from '../sites/mapTiles'
import { deductionsPdf, deductionsPdfName, type DeductionImages, type Deductions, type Jpeg } from './deductionsPdf'

type User = Parameters<typeof apiFetch>[0]

const PHOTO_MAX = 720
const MAP_W = 460
const MAP_H = 340
// Close enough on satellite to see the rows; the street tiles stop at 18 here.
const SATELLITE_ZOOM = 19
const STREET_ZOOM = 18
const TILE = 256

async function canvasJpeg(canvas: HTMLCanvasElement, quality = 0.75): Promise<Jpeg | null> {
  const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/jpeg', quality))
  if (!blob) return null
  return { bytes: new Uint8Array(await blob.arrayBuffer()), w: canvas.width, h: canvas.height }
}

// A photo scaled down and re-encoded, upright (createImageBitmap applies the
// camera's orientation).
async function photoJpeg(user: User, record: string, idx: number): Promise<Jpeg | null> {
  const r = await apiFetch(user, `/api/reports/contractor/photo/${encodeURIComponent(record)}/${idx}`)
  if (!r.ok) return null
  const bmp = await createImageBitmap(await r.blob())
  const k = Math.min(1, PHOTO_MAX / Math.max(bmp.width, bmp.height))
  const c = document.createElement('canvas')
  c.width = Math.max(1, Math.round(bmp.width * k))
  c.height = Math.max(1, Math.round(bmp.height * k))
  c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height)
  bmp.close?.()
  return canvasJpeg(c, 0.72)
}

interface Tiles {
  zoom: number
  url: (z: number, x: number, y: number) => string
  // What the map's corner credits, given where it is.
  credit: (gps: { lat: number; lng: number }) => Promise<string>
}

// Satellite when Google gives a session, else the street tiles.
async function tileSet(): Promise<Tiles> {
  const key = googleMapsKey()
  const s = key ? await googleSession(key) : null
  if (key && s) {
    const q = `session=${encodeURIComponent(s.session)}&key=${encodeURIComponent(key)}`
    return {
      zoom: SATELLITE_ZOOM,
      url: (z, x, y) => `${TILE_BASE}/v1/2dtiles/${z}/${x}/${y}?${q}`,
      // Google's terms ask for the copyright of the imagery in view.
      credit: async (gps) => {
        const d = 0.001
        const v = new URLSearchParams({ zoom: String(SATELLITE_ZOOM), north: String(gps.lat + d), south: String(gps.lat - d), east: String(gps.lng + d), west: String(gps.lng - d) })
        const r = await fetch(`${TILE_BASE}/tile/v1/viewport?${v}&${q}`).catch(() => null)
        const text = r?.ok ? String(((await r.json().catch(() => null)) as { copyright?: string } | null)?.copyright || '') : ''
        return `Google${text ? ` · ${text}` : ''}`
      },
    }
  }
  const t = tileSource()
  return {
    zoom: STREET_ZOOM,
    url: (z, x, y) => t.url.replace('{s}', 'a').replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y)),
    credit: async () => '© OpenStreetMap contributors',
  }
}

// Tiles are shared between neighbouring picks, so each is fetched once a run.
function tileLoader(t: Tiles) {
  const cache = new Map<string, Promise<HTMLImageElement | null>>()
  return (z: number, x: number, y: number) => {
    const n = 2 ** z
    const url = t.url(z, ((x % n) + n) % n, y)
    if (!cache.has(url)) {
      cache.set(
        url,
        new Promise((ok) => {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.onload = () => ok(img)
          img.onerror = () => ok(null)
          img.src = url
        }),
      )
    }
    return cache.get(url)!
  }
}

// The pick's point in the middle of the street map, with a pin on it.
async function mapJpeg(t: Tiles, tile: ReturnType<typeof tileLoader>, gps: { lat: number; lng: number }): Promise<Jpeg | null> {
  const ZOOM = t.zoom
  const n = 2 ** ZOOM * TILE
  const s = Math.sin((gps.lat * Math.PI) / 180)
  const px = ((gps.lng + 180) / 360) * n
  const py = (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n
  const left = px - MAP_W / 2
  const top = py - MAP_H / 2
  const c = document.createElement('canvas')
  c.width = MAP_W
  c.height = MAP_H
  const g = c.getContext('2d')!
  g.fillStyle = '#efebe4'
  g.fillRect(0, 0, MAP_W, MAP_H)
  const jobs: Promise<boolean>[] = []
  for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + MAP_W) / TILE); tx++) {
    for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + MAP_H) / TILE); ty++) {
      jobs.push(
        tile(ZOOM, tx, ty).then((img) => {
          if (img) g.drawImage(img, tx * TILE - left, ty * TILE - top)
          return !!img
        }),
      )
    }
  }
  if (!(await Promise.all(jobs)).some(Boolean)) return null
  const cx = MAP_W / 2
  const cy = MAP_H / 2
  g.beginPath()
  g.arc(cx, cy, 11, 0, Math.PI * 2)
  g.fillStyle = '#ffffff'
  g.fill()
  g.beginPath()
  g.arc(cx, cy, 8, 0, Math.PI * 2)
  g.fillStyle = '#c0392b'
  g.fill()
  g.font = '14px sans-serif'
  let credit = await t.credit(gps)
  // A long imagery copyright is cut to fit the map.
  while (credit.length > 8 && g.measureText(credit).width > MAP_W - 20) credit = `${credit.slice(0, -2)}…`
  const w = g.measureText(credit).width
  g.fillStyle = 'rgba(255,255,255,0.8)'
  g.fillRect(MAP_W - w - 10, MAP_H - 20, w + 10, 20)
  g.fillStyle = '#333333'
  g.fillText(credit, MAP_W - w - 5, MAP_H - 6)
  try {
    return await canvasJpeg(c, 0.8)
  } catch {
    // A tile served without CORS taints the canvas.
    return null
  }
}

export function deductionsTask(user: User, q: { from: string; to: string; contractor: string }, note: string, printedOn: string): StartTask {
  const p = new URLSearchParams({ from: q.from, to: q.to })
  if (q.contractor) p.set('contractor', q.contractor)
  const placeholder = deductionsPdfName({ from: q.from, to: q.to, contractor: q.contractor || null, items: [] })
  return {
    label: 'Quality Control PDF',
    note,
    file: placeholder,
    run: async (progress) => {
      const d = await apiJson<Deductions>(user, `/api/reports/contractor/deductions?${p}`)
      const tiles = await tileSet()
      const tile = tileLoader(tiles)
      const images: DeductionImages[] = []
      for (const [i, x] of d.items.entries()) {
        const [map, ...photos] = await Promise.all([
          x.gps ? mapJpeg(tiles, tile, x.gps).catch(() => null) : Promise.resolve(null),
          ...x.photos.map((ph) => photoJpeg(user, ph.record, ph.idx).catch(() => null)),
        ])
        const got = photos.filter((j): j is Jpeg => !!j)
        images.push({ map, photos: got, missing: photos.length - got.length })
        progress(Math.round(((i + 1) / d.items.length) * 100))
      }
      return { file: { name: deductionsPdfName(d), content: deductionsPdf(d, images, printedOn), type: 'application/pdf' } }
    },
  }
}
